import { Box, Button, Card, Chip, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import {
  Datagrid,
  FilterButton,
  FunctionField,
  List,
  TextField,
  TopToolbar,
  useListContext,
  useNotify,
  useTranslate,
} from 'react-admin';
import { downloadBlob } from 'src/utils/format';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { paths } from '../../../../../routes/paths';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import ErrorPage403 from '../../../../error/403';
import { SECTION_CARD_SX } from '../shared/sectionCardSx';
import FilterGroup from '../../../components/FilterGroup';
import XTextInput from '../../../components/XTextInput';
import { XDateTimeInput } from '../../../components/XDateTimeInput';
import { Resources } from '../../resource';
import {
  bbExportUsdCRewardFeedCsv,
  bbGetUsdCRewardFeed,
  BbUsdcRewardFeedResponse,
} from '../../../services/blindBoxService';
import { normalizeUsdCRewardFeedBody } from './dataProvider';

function UsdcBalanceChip() {
  const { filterValues } = useListContext();
  const userId = String((filterValues as any)?.userId || '').trim();
  const from = String((filterValues as any)?.from || '').trim();
  const to = String((filterValues as any)?.to || '').trim();
  const [usdc, setUsdc] = useState<string | number | null | undefined>(null);

  useEffect(() => {
    if (!userId) {
      setUsdc(null);
      return;
    }
    bbGetUsdCRewardFeed({
      userId,
      from: from || undefined,
      to: to || undefined,
      limit: 1,
      offset: 0,
    })
      .then((r: BbUsdcRewardFeedResponse) => setUsdc(r.userBalance?.usdcBalance ?? null))
      .catch(() => setUsdc(null));
  }, [userId, from, to]);

  if (usdc === null || usdc === undefined) return null;

  return (
    <Box sx={{ px: 2, pb: 1 }}>
      <Chip size="small" label={`usdcBalance: ${usdc}`} />
    </Box>
  );
}

function RewardFeedUsdcCsvExportButton() {
  const { filterValues } = useListContext();
  const notify = useNotify();
  const t = useTranslate();
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const body = normalizeUsdCRewardFeedBody({ ...(filterValues as Record<string, unknown>) });
      body.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { blob, filename } = await bbExportUsdCRewardFeedCsv(body);
      downloadBlob(filename, blob);
      notify(t('module.dashboardV2.exportSuccess', { _: 'Export success' }), { type: 'info' });
    } catch (e: any) {
      notify(e?.message ?? t('module.dashboardV2.exportFailed', { _: 'Export failed' }), { type: 'warning' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      variant="contained"
      onClick={handleExport}
      disabled={exporting}
      startIcon={exporting ? <CircularProgress size={16} sx={{ color: 'common.white' }} /> : null}
      sx={{
        height: 36,
        borderRadius: 0.75,
        px: 2.2,
        textTransform: 'none',
        fontSize: 14,
        fontWeight: 700,
        bgcolor: '#18181B',
        '&:hover': { bgcolor: 'grey.900' },
      }}
    >
      {t('module.dashboardV2.csvExport', { _: 'CSV Export' })}
    </Button>
  );
}

export default function BlindBoxRewardFeedUsdcList() {
  const t = useTranslate();

  const filters = useMemo(
    () => [
      <FilterGroup alwaysOn p={0} key="dt">
        <XDateTimeInput source="from" label="from" type="from" />
        <XDateTimeInput source="to" label="to" type="to" />
      </FilterGroup>,
      <XTextInput alwaysOn source="userId" label="userId" key="userId" />,
    ],
    []
  );

  return (
    <PermissionGuard resource="bb-reward-feed-usdc" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.blindBox.rewardFeedUsdc')}
          links={[
            { name: t('module.blindBox.title'), href: paths.blindBox.overview.root },
            { name: t('module.blindBox.rewardFeedUsdc') },
          ]}
        />

        <Card variant="outlined" sx={{ ...SECTION_CARD_SX, mt: 2 }}>
          <List
            resource={Resources.blindBoxRewardFeedUsdc}
            exporter={false}
            perPage={50}
            actions={
              <TopToolbar>
                <FilterButton />
                <RewardFeedUsdcCsvExportButton />
              </TopToolbar>
            }
            filters={filters as any}
            sort={{ field: 'createdAt', order: 'DESC' }}
          >
            <Box sx={{ px: 2, pt: 1 }}>
              <Chip size="small" variant="outlined" label={t('module.blindBox.rewardFeedUsdcHint')} />
            </Box>
            <UsdcBalanceChip />
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                bulkActionButtons={false}
                sx={{
                  '& .MuiTableCell-root': {
                    whiteSpace: 'nowrap',
                    wordBreak: 'normal',
                  },
                }}
              >
                <FunctionField
                  label="createdAt"
                  render={(r: any) => (r?.createdAt ? dayjs(r.createdAt).format('YYYY-MM-DD HH:mm:ss') : '—')}
                />
                <TextField source="id" label="id" />
                <TextField source="userId" label="userId" />
                <TextField source="walletAddress" label="walletAddress" />
                <TextField source="source" label="source" />
                <TextField source="status" label="status" />
                <TextField source="rewardName" label="rewardName" />
                <TextField source="amount" label="amount" />
                <TextField source="tokenType" label="tokenType" />
                <TextField source="issueId" label="issueId" />
                <TextField source="issueNumber" label="issueNumber" />
                <TextField source="seasonId" label="seasonId" />
              </Datagrid>
            </Box>
          </List>
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
}
